/**
 * @file PrivacyPolicy.js — Page statique de politique de confidentialité (R-0126 RGPD).
 * Décrit les données collectées, les finalités du traitement, la base juridique,
 * la durée de conservation et les droits des utilisateurs conformément au RGPD.
 * Le contenu est en anglais ; l'internationalisation (i18n) sera ajoutée ultérieurement.
 */
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

/**
 * Composant de la page de politique de confidentialité.
 * Affiche un document structuré en sections avec ancres implicites,
 * conforme aux exigences RGPD pour une plateforme communautaire.
 * @returns {React.ReactElement}
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ── Bouton de retour ── */}
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="mb-8 text-muted-foreground hover:text-foreground gap-1.5"
        >
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </Button>

        {/* ── En-tête ── */}
        <h1
          className="text-3xl sm:text-4xl font-medium tracking-tight text-foreground mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Privacy Policy
        </h1>
        <p className="text-sm text-muted-foreground mb-10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Last updated: April 2026
        </p>

        {/* ── Contenu structuré en sections ── */}
        <div className="space-y-10 text-sm leading-relaxed text-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>

          {/* Section 1 — Données collectées */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">1. Data We Collect</h2>
            <p className="text-muted-foreground mb-3">
              When you create an account on Circles (Ringo), we collect the following personal data:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-muted-foreground">
              <li><strong className="text-foreground">Full name</strong> — used to identify you within your circles.</li>
              <li><strong className="text-foreground">Email address</strong> — used for authentication and notifications.</li>
              <li><strong className="text-foreground">Phone number</strong> (optional) — used for event-related communication if provided.</li>
            </ul>
            <p className="mt-3 text-muted-foreground">
              All personally identifiable information (PII) is <strong className="text-foreground">encrypted at rest</strong> in our database.
              This means your name, email, and phone number are stored in encrypted form and are only decrypted
              when needed to serve your requests.
            </p>
          </section>

          {/* Section 2 — Finalités du traitement */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">2. Purpose of Processing</h2>
            <p className="text-muted-foreground">
              We process your personal data for the following purposes:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 mt-3 text-muted-foreground">
              <li><strong className="text-foreground">Community management</strong> — managing circle memberships, roles, and permissions.</li>
              <li><strong className="text-foreground">Event organization</strong> — creating events, managing sign-ups, and sending event-related notifications.</li>
              <li><strong className="text-foreground">Authentication and security</strong> — verifying your identity, managing sessions, and enforcing two-factor authentication.</li>
            </ul>
          </section>

          {/* Section 3 — Base juridique */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">3. Legal Basis</h2>
            <p className="text-muted-foreground">
              Under the General Data Protection Regulation (GDPR), we rely on the following legal bases:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 mt-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">Consent</strong> (Art. 6(1)(a)) — you provide your data voluntarily when
                creating an account and can withdraw consent at any time by deleting your account.
              </li>
              <li>
                <strong className="text-foreground">Legitimate interest</strong> (Art. 6(1)(f)) — we have a legitimate interest
                in operating the platform securely and preventing abuse.
              </li>
            </ul>
          </section>

          {/* Section 4 — Durée de conservation */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">4. Data Retention</h2>
            <p className="text-muted-foreground">
              Your personal data is retained for as long as your account exists. When you delete your account,
              all associated personal data is permanently removed from our systems. Anonymized, aggregated
              statistics (such as event attendance counts) may be retained indefinitely as they cannot be linked
              back to you.
            </p>
          </section>

          {/* Section 5 — Droits des utilisateurs */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">5. Your Rights</h2>
            <p className="text-muted-foreground mb-3">
              Under the GDPR, you have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-muted-foreground">
              <li><strong className="text-foreground">Right of access</strong> — you can request a copy of the data we hold about you.</li>
              <li><strong className="text-foreground">Right to rectification</strong> — you can update your personal information from your profile page at any time.</li>
              <li><strong className="text-foreground">Right to erasure</strong> — you can request the deletion of your account and all associated data.</li>
              <li><strong className="text-foreground">Right to data portability</strong> — you can request an export of your data in a machine-readable format.</li>
            </ul>
            <p className="mt-3 text-muted-foreground">
              To exercise any of these rights, please contact us using the information below.
            </p>
          </section>

          {/* Section 6 — Contact */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">6. Contact</h2>
            <p className="text-muted-foreground">
              If you have any questions about this privacy policy or wish to exercise your data protection rights,
              please contact us at:
            </p>
            <p className="mt-3 text-muted-foreground">
              <strong className="text-foreground">Email:</strong>{' '}
              <a
                href="mailto:privacy@ringo.leomols.be"
                className="text-foreground underline underline-offset-4 hover:text-foreground/80"
              >
                privacy@ringo.leomols.be
              </a>
            </p>
          </section>

          {/* ── Séparateur et lien vers la politique de cookies ── */}
          <div className="border-t border-border pt-6">
            <p className="text-muted-foreground">
              See also our{' '}
              <Link
                to="/cookies"
                className="text-foreground underline underline-offset-4 hover:text-foreground/80"
              >
                Cookie Policy
              </Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
